///
/// EarthBrowser
///
/// Created by Matt Giger on 4/10/2026
/// Copyright (c) 2026 Geodata Labs
///

import { isInfoPanelProvider } from "@/earthbrowser/datasets/InfoPanelProvider"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { GestureState } from "@stovokor/gestures/GestureRecognizer"
import { type HoverGestureEventData, HoverGestureRecognizer } from "@stovokor/gestures/HoverGestureRecognizer"

/**
 * Optional owner hook: nodes that want a visual hover cue (e.g. brightening
 * their marker so the user can see which one a tap would pick in a dense
 * cluster) implement this and get notified alongside the preview callout
 */
export interface HoverHighlightTarget {
	onHoverHighlight(hovered: boolean): void
}

function isHoverHighlightTarget(node: unknown): node is HoverHighlightTarget {
	return !!node && typeof (node as HoverHighlightTarget).onHoverHighlight === "function"
}

export class EntityHoverHandler extends HoverGestureRecognizer {
	private context: EarthBrowserContext

	constructor(context: EarthBrowserContext, priority: number = 5) {
		super()
		this.context = context
		this.priority = priority

		this.onGesture((data: HoverGestureEventData) => {
			switch (data.state) {
				case GestureState.Began:
				case GestureState.Changed:
					this.showPreview()
					this.setHighlight(true)
					break
				case GestureState.Ended:
					this.hidePreview()
					this.setHighlight(false)
					break
			}
		})
	}

	private setHighlight(hovered: boolean): void {
		if (isHoverHighlightTarget(this.owner)) {
			this.owner.onHoverHighlight(hovered)
		}
	}

	private showPreview(): void {
		if (!this.owner || !isInfoPanelProvider(this.owner)) {
			return
		}

		// While a dataset element is focused its info panel is showing, so
		// never stack the hover tooltip on top of it (for any entity)
		const focused = this.context.focusManager.getFocusedNode()
		if (focused && isInfoPanelProvider(focused)) {
			this.context.hoveredObjectCallout?.hide()
			return
		}

		this.context.hoveredObjectCallout?.previewFor(this.owner)
	}

	private hidePreview(): void {
		this.context.hoveredObjectCallout?.hidePreview(this.owner)
	}
}
